import { ActionTree, ActionContext } from 'vuex';
import { RootState } from '@/store';
import { State } from './state';
import { state as ProspectState } from '../prospectView/state';
import { Mutations } from './mutations';
import { ClientViewMutationTypes } from './mutation-types';
import { ClientViewActionTypes } from './action-types';
import { store } from '../../index';
import { get, forEach, pull } from 'lodash';
import {
  PortfoliosApiResult,
  PortfoliosApiResponse,
  PortfoliosApiOptions,
  ClientApiOptions,
  ClientDetailsApiResponse,
  ClientFamilyApiResponse,
  PortfolioApiResponse,
  PortfolioApiOptions,
  StandardFeesApiResponse,
  CreateScenarioPostBody,
  ScenarioApiOptions,
  PortfolioScenarioApiOptions,
  UpdateScenarioBody,
  ScenarioCoreDetailsResponse,
  ScenarioResponse,
  ApprovalSubmitOptions,
  PricingReviewRequest,
} from './types';

import ServerAPI, { handleError } from '@/services/serverAPI';
import { ProspectActions } from '../prospectView/action-types';
import { KpiActionTypes } from '../kpi/action-types';
import { HistoryActionTypes } from '@/store/modules/history/action-types';

type AugmentedAction = {
  commit<K extends keyof Mutations>(
    key: K,
    payload: Parameters<Mutations[K]>[1]
  ): ReturnType<Mutations[K]>;
} & Omit<ActionContext<State, RootState>, 'commit'>;

export interface Actions {
  [ClientViewActionTypes.GET_PORTFOLIOS](
    { commit }: AugmentedAction,
    payload: PortfoliosApiOptions
  ): Promise<void>;
  [ClientViewActionTypes.GET_PORTFOLIO](
    { commit }: AugmentedAction,
    payload: PortfolioApiOptions
  ): Promise<void>;
  [ClientViewActionTypes.GET_CLIENT_DETAILS](
    { commit }: AugmentedAction,
    payload: ClientApiOptions
  ): Promise<void>;
  [ClientViewActionTypes.GET_CLIENT_FAMILY](
    { commit }: AugmentedAction,
    payload: ClientApiOptions
  ): Promise<void>;
  [ClientViewActionTypes.GET_SCENARIO_LIST](
    { commit }: AugmentedAction,
    payload: PortfoliosApiOptions
  ): Promise<void>;
  [ClientViewActionTypes.CREATE_SCENARIO](
    { commit }: AugmentedAction,
    payload: CreateScenarioPostBody
  ): Promise<ScenarioApiOptions>;
  [ClientViewActionTypes.GET_SCENARIO](
    { commit }: AugmentedAction,
    payload: ScenarioApiOptions
  ): Promise<void>;
  [ClientViewActionTypes.UPDATE_SCENARIO](
    { commit }: AugmentedAction,
    payload: UpdateScenarioBody
  ): Promise<void>;
  [ClientViewActionTypes.DELETE_SCENARIO](
    { commit }: AugmentedAction,
    payload: PortfolioScenarioApiOptions
  ): Promise<void>;
  [ClientViewActionTypes.CLEAR_CLIENT_STATE](
    { commit }: AugmentedAction,
    payload: undefined
  ): Promise<void>;
  [ClientViewActionTypes.APPROVAL_SUBMIT](
    { commit }: AugmentedAction,
    payload: ApprovalSubmitOptions
  ): Promise<void>;
  [ClientViewActionTypes.APPROVE_SCENARIO](
    { commit }: AugmentedAction,
    payload: PortfolioScenarioApiOptions
  ): Promise<void>;
  [ClientViewActionTypes.REJECT_SCENARIO](
    { commit }: AugmentedAction,
    payload: ApprovalSubmitOptions
  ): Promise<void>;
  [ClientViewActionTypes.REQUEST_PRICING_REVIEW](
    { commit }: AugmentedAction,
    payload: PricingReviewRequest
  ): Promise<boolean>;
  [ClientViewActionTypes.COMPLETE_SCENARIO_REMINDER](
    { commit }: AugmentedAction,
    scenarioId: string
  ): Promise<boolean>;
}

export const actions: ActionTree<State, RootState> & Actions = {
  async [ClientViewActionTypes.GET_PORTFOLIOS](
    { commit },
    apiOptions: PortfoliosApiOptions
  ) {
    try {
      commit(ClientViewMutationTypes.SET_PORTFOLIOS_LOADING, true);

      const portfolioData: PortfoliosApiResponse = await ServerAPI.get(
        `/portfolios?filters=clientId:${apiOptions.clientId}&sort_by=is_main_portfolio&sort_descending=true`
      );

      commit(ClientViewMutationTypes.SET_PORTFOLIO_LIST, {
        portfolios: portfolioData.results as Array<PortfoliosApiResult>,
      });
    } catch (e) {
      handleError(e);
    } finally {
      commit(ClientViewMutationTypes.SET_PORTFOLIOS_LOADING, false);
    }
  },
  async [ClientViewActionTypes.GET_CLIENT_DETAILS](
    { commit },
    payload: ClientApiOptions
  ) {
    try {
      commit(ClientViewMutationTypes.SET_CLIENT_ERROR, false);
      commit(ClientViewMutationTypes.SET_CLIENT_LOADING, true);

      const clientDetails: ClientDetailsApiResponse = await ServerAPI.get(
        `/clients/${payload.clientId}/details`
      );

      commit(
        ClientViewMutationTypes.SET_CLIENT_DETAILS,
        clientDetails as ClientDetailsApiResponse
      );
    } catch (e) {
      commit(ClientViewMutationTypes.SET_CLIENT_ERROR, true);
      handleError(e);
    } finally {
      commit(ClientViewMutationTypes.SET_CLIENT_LOADING, false);
    }
  },
  async [ClientViewActionTypes.GET_CLIENT_FAMILY](
    { commit },
    payload: ClientApiOptions
  ) {
    try {
      const clientFamily: ClientFamilyApiResponse = await ServerAPI.get(
        `/clients/${payload.clientId}/family-members`
      );

      commit(
        ClientViewMutationTypes.SET_CLIENT_FAMILY,
        clientFamily as ClientFamilyApiResponse
      );
    } catch (e) {
      handleError(e);
    }
  },
  async [ClientViewActionTypes.CLEAR_CLIENT_STATE]({ commit }) {
    commit(ClientViewMutationTypes.CLEAR_STATE, null);
  },
  async [ClientViewActionTypes.GET_PORTFOLIO](
    { commit },
    apiOptions: PortfolioApiOptions
  ) {
    try {
      commit(ClientViewMutationTypes.SET_PORTFOLIO_LOADING, true);

      const portfolioData: PortfolioApiResponse = await ServerAPI.get(
        `/portfolios/${apiOptions.portfolioId}`
      );

      // This adds transactionsBreakdown.transactionsByType to an object for the portfolio field config to read
      const portfolioState = portfolioData as PortfolioApiResponse;
      const transactionBreakdown = get(
        portfolioState,
        'transactionsBreakdown.transactionsByType',
        []
      );

      if (transactionBreakdown && transactionBreakdown.length > 0) {
        portfolioState.transactionsBreakdown.transactionsByTypeObj = {};
        forEach(transactionBreakdown, item => {
          portfolioState.transactionsBreakdown.transactionsByTypeObj[
            `${item.transactionType.dataValue}`
          ] = {};
          forEach(pull(Object.keys(item), 'transactionType'), subItem => {
            portfolioState.transactionsBreakdown.transactionsByTypeObj[
              `${item.transactionType.dataValue}`
            ][subItem] = item[subItem];
          });
        });
      }

      if (apiOptions.portfolioId !== 'default') {
        store.dispatch(ClientViewActionTypes.GET_STANDARD_FEES, {
          portfolioId: apiOptions.portfolioId,
        } as PortfolioApiOptions);
      }

      commit(ClientViewMutationTypes.SET_PORTFOLIO, {
        portfolio: portfolioState,
      });
    } catch (e) {
      handleError(e);
      commit(ClientViewMutationTypes.SET_PORTFOLIO, {
        portfolio: {} as PortfolioApiResponse,
      });
    } finally {
      commit(ClientViewMutationTypes.SET_PORTFOLIO_LOADING, false);
    }
  },
  async [ClientViewActionTypes.GET_STANDARD_FEES](
    { commit },
    apiOptions: PortfolioApiOptions
  ) {
    try {
      const standardFeesData: PortfolioApiResponse = await ServerAPI.get(
        `/standard-fees/${apiOptions.portfolioId}`
      );
      commit(ClientViewMutationTypes.SET_STANDARD_FEES, {
        standardFees: standardFeesData as StandardFeesApiResponse,
      });
    } catch (e) {
      handleError(e);
      commit(ClientViewMutationTypes.SET_STANDARD_FEES, {
        standardFees: {} as StandardFeesApiResponse,
      });
    }
  },
  async [ClientViewActionTypes.GET_SCENARIO_LIST](
    { commit },
    payload: PortfoliosApiOptions
  ) {
    try {
      const scenarioList: Array<ScenarioCoreDetailsResponse> = await ServerAPI.get(
        `/scenario?filter_field=portfolioId&filter_id=${payload.preLoadPortfolioId}`
      );
      commit(ClientViewMutationTypes.SET_SCENARIO_LIST, scenarioList);
    } catch (e) {
      handleError(e);
    }
  },
  async [ClientViewActionTypes.CREATE_SCENARIO](
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { commit },
    payload: CreateScenarioPostBody
  ) {
    try {
      const newScenario = await ServerAPI.post(`/scenario`, payload);
      store.dispatch(ClientViewActionTypes.GET_SCENARIO_LIST, {
        preLoadPortfolioId: payload.portfolioId,
      } as PortfoliosApiOptions);
      return newScenario;
    } catch (e) {
      handleError(e);
    }
  },
  async [ClientViewActionTypes.GET_SCENARIO](
    { commit },
    payload: ScenarioApiOptions
  ) {
    try {
      commit(ClientViewMutationTypes.SET_SCENARIO_LOADING, true);

      const scenario: ScenarioResponse = await ServerAPI.get(
        `/scenario/${payload.scenarioId}`
      );

      store.dispatch(HistoryActionTypes.GET_SCENARIO_HISTORY, {
        scenarioId: payload.scenarioId,
      });
      commit(ClientViewMutationTypes.SET_SCENARIO, scenario);
    } catch (e) {
      handleError(e);
    } finally {
      commit(ClientViewMutationTypes.SET_SCENARIO_LOADING, false);
    }
  },
  async [ClientViewActionTypes.DELETE_SCENARIO](
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { commit },
    payload: PortfolioScenarioApiOptions
  ) {
    try {
      await ServerAPI.delete(`/scenario/${payload.scenarioId}`);
    } catch (e) {
      handleError(e);
    }
    if (payload.portfolioId) {
      store.dispatch(ClientViewActionTypes.GET_SCENARIO_LIST, {
        preLoadPortfolioId: payload.portfolioId,
      } as PortfoliosApiOptions);
    }
  },
  async [ClientViewActionTypes.UPDATE_SCENARIO](
    { commit, state },
    payload: UpdateScenarioBody
  ) {
    document.title = '* ' + document.title;
    commit(ClientViewMutationTypes.SET_SCENARIO_SAVING, true);
    const scenarioId = state.scenario.scenario.scenarioId.dataValue;
    try {
      let path = `/scenario/${scenarioId}`;
      if (payload.resetFlag) {
        path += `?reset_scenario=true`;
      }
      const scenario = await ServerAPI.patch(path, payload.update);
      commit(ClientViewMutationTypes.SET_SCENARIO, scenario);

      if (payload.isProspectPortfolio) {
        store.dispatch(ProspectActions.GET_PROSPECT_PORTFOLIOS, {
          clientId: ProspectState.selectedClientId,
          scenarioId,
        });
      } else {
        store.dispatch(ClientViewActionTypes.GET_SCENARIO_LIST, {
          preLoadPortfolioId: state.portfolio.portfolioId.dataValue,
        } as PortfoliosApiOptions);
      }

      if (payload.isProspect) {
        store.dispatch(KpiActionTypes.GET_PROSPECT_KPI_BY_IDS);
      }
    } catch (e) {
      handleError(e);
    }
    document.title = document.title.replace('* ', '');
    commit(ClientViewMutationTypes.SET_SCENARIO_SAVING, false);
  },
  async [ClientViewActionTypes.APPROVAL_SUBMIT](
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { commit },
    payload: ApprovalSubmitOptions
  ) {
    try {
      if (payload && payload.scenarioId) {
        await ServerAPI.post(`/scenario/${payload.scenarioId}/submit`, {
          details: payload.message,
          reminderDate: payload.reminderDate,
          notifyClient: payload.notifyClient,
          meetingDate: payload.meetingDate,
        });
        store.dispatch(ClientViewActionTypes.GET_SCENARIO, {
          scenarioId: payload.scenarioId,
        } as ScenarioApiOptions);

        if (payload.portfolioId) {
          store.dispatch(ClientViewActionTypes.GET_SCENARIO_LIST, {
            preLoadPortfolioId: payload.portfolioId,
          } as PortfoliosApiOptions);
        } else {
          alert(`Error: There is no portfolio id selected`);
        }
        if (payload.clientId) {
          store.dispatch(ProspectActions.GET_PROSPECT_PORTFOLIOS, {
            clientId: payload.clientId,
            scenarioId: payload.scenarioId,
          });
        }
      } else {
        alert(`Error: There is no scenario id selected`);
      }
    } catch (e) {
      handleError(e);
    }
  },
  async [ClientViewActionTypes.APPROVE_SCENARIO](
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { commit },
    payload: PortfolioScenarioApiOptions
  ) {
    try {
      if (payload && payload.scenarioId) {
        await ServerAPI.post(`/scenario/${payload.scenarioId}/approve`, {
          onBehalfOfUserRole: payload.onBehalfOf,
        });

        store.dispatch(HistoryActionTypes.GET_SCENARIO_HISTORY, {
          scenarioId: payload.scenarioId,
        });
      } else {
        alert(`Error: There is no scenario id selected`);
      }
    } catch (e) {
      handleError(e);
    }
  },
  async [ClientViewActionTypes.REJECT_SCENARIO](
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { commit },
    payload: ApprovalSubmitOptions
  ) {
    try {
      if (payload && payload.scenarioId) {
        await ServerAPI.post(`/scenario/${payload.scenarioId}/reject`, {
          details: payload.message,
          onBehalfOfUserRole: payload.onBehalfOf,
        });

        store.dispatch(HistoryActionTypes.GET_SCENARIO_HISTORY, {
          scenarioId: payload.scenarioId,
        });
      } else {
        alert(`Error: There is no scenario id selected`);
      }
    } catch (e) {
      handleError(e);
    }
  },
  async [ClientViewActionTypes.REQUEST_PRICING_REVIEW](
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { commit },
    payload: PricingReviewRequest
  ) {
    try {
      await ServerAPI.post(`/portfolios/review`, {
        ids: payload.portfolioIds,
      });
      return true;
    } catch (e) {
      handleError(e);
      return false;
    }
  },
  async [ClientViewActionTypes.COMPLETE_SCENARIO_REMINDER](
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { commit },
    scenarioId: string
  ) {
    try {
      await ServerAPI.put(`/scenario/${scenarioId}/reminder`);
      return true;
    } catch (e) {
      handleError(e);
      return false;
    }
  },
};
